/*
 * SolidForms 3D-Printing Course: sales page behaviour.
 *
 * Everything on the page works without this file: the CTAs are plain anchors,
 * the FAQ is <details>, and the enrol form is Shopify's own product form that
 * posts straight to checkout. This adds the scroll story on top:
 *
 *   - reveal as sections arrive, and the proof figures counting up
 *   - a reading-progress line, and a chapter rail on wide screens
 *   - the path's line filling as the reader moves through the sessions
 *   - smooth jumps to the price card and between chapters
 *   - the sticky price bar, the optional launch countdown, and the Meta Pixel
 *     funnel events (Lead on a CTA, InitiateCheckout on enrol)
 *
 * All scroll-linked work runs from one passive listener, batched into a
 * single animation frame.
 */
(function () {
  'use strict';

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* No-op unless a Meta Pixel is present in this window. */
  function track(event, data) {
    try {
      if (typeof window.fbq === 'function') window.fbq('track', event, data || {});
    } catch (e) {}
  }

  /*
   * 23:59:59 in Cairo on the given day, as an absolute instant, so the
   * deadline is the same moment for every visitor wherever they are. Egypt
   * observes summer time, so the offset is read from the zone, not assumed.
   */
  function cairoEndOfDay(ymd) {
    var guess = Date.parse(ymd + 'T23:59:59Z');
    if (isNaN(guess)) return NaN;
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Africa/Cairo', hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit'
      }).formatToParts(new Date(guess));
      var p = {};
      parts.forEach(function (x) { p[x.type] = x.value; });
      var wall = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
      return guess - (wall - guess);
    } catch (e) {
      return guess - 2 * 3600 * 1000;
    }
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function clamp(n) { return n < 0 ? 0 : n > 1 ? 1 : n; }

  /*
   * "800+" counts 0 → 800 and keeps the "+". Each figure sits centred in a
   * fixed grid cell, so its changing width never moves anything around it.
   */
  function countUp(el) {
    var txt = el.textContent.trim();
    var m = txt.match(/^(\D*?)(\d[\d,]*)(.*)$/);
    if (!m) return;
    var target = parseInt(m[2].replace(/,/g, ''), 10);
    if (!(target > 9)) return;
    var comma = m[2].indexOf(',') > -1;
    var fmt = function (n) { return comma ? n.toLocaleString('en-US') : String(n); };
    var dur = 1400, t0 = null;
    el.textContent = m[1] + fmt(0) + m[3];
    var step = function (t) {
      if (t0 === null) t0 = t;
      var k = clamp((t - t0) / dur);
      var eased = 1 - Math.pow(1 - k, 3);
      el.textContent = m[1] + fmt(Math.round(target * eased)) + m[3];
      if (k < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  function Course(root) {
    if (root.sfco) return;
    root.sfco = true;
    root.classList.add('is-js');

    var hero = root.querySelector('[data-sfco-hero]');
    var fin = root.querySelector('[data-sfco-final]');
    var card = root.querySelector('#sfco-card');

    /* ------------------------------------------------ reveal + count-up */
    // The hero animates in CSS; it is marked revealed in the same task that
    // turns the hidden state on, so it is never hidden for a frame.
    var items = root.querySelectorAll('[data-rv]');
    var counters = root.querySelectorAll('[data-count]');
    if (!hasIO || reduce) {
      items.forEach(function (el) { el.classList.add('is-in'); });
    } else {
      if (hero) hero.querySelectorAll('[data-rv]').forEach(function (el) { el.classList.add('is-in'); });
      var rv = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('is-in');
          rv.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
      items.forEach(function (el) { if (!el.classList.contains('is-in')) rv.observe(el); });

      var cu = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (!e.isIntersecting) return;
          cu.unobserve(e.target);
          countUp(e.target);
        });
      }, { threshold: 0.6 });
      counters.forEach(function (el) { cu.observe(el); });
    }

    /* ------------------------------------------------ smooth jumps + Lead */
    // To the price card: centred when it fits, so the price and the button
    // land together; otherwise its top clears the header. To a chapter: its
    // top, honouring the section's scroll-margin.
    root.addEventListener('click', function (e) {
      var a = e.target.closest('a[href^="#sfco-"]');
      if (!a || !root.contains(a)) return;
      var target = root.querySelector(a.getAttribute('href'));
      if (!target) return;
      e.preventDefault();
      var behavior = reduce ? 'auto' : 'smooth';
      if (target === card) {
        track('Lead', { content_name: 'SolidForms 3D-Printing Course' });
        var fits = card.offsetHeight < window.innerHeight - 140;
        card.scrollIntoView({ behavior: behavior, block: fits ? 'center' : 'start' });
        var focus = card.querySelector('input[name="id"]:checked, [data-sfco-buy]');
        if (focus) setTimeout(function () { focus.focus({ preventScroll: true }); }, reduce ? 0 : 600);
      } else {
        target.scrollIntoView({ behavior: behavior, block: 'start' });
      }
    });

    /* ------------------------------------------------ enrol → InitiateCheckout */
    var form = root.querySelector('[data-sfco-form]');
    if (form) {
      form.addEventListener('submit', function () {
        track('InitiateCheckout', {
          content_name: 'SolidForms 3D-Printing Course',
          value: parseFloat(root.getAttribute('data-value')) || 0,
          currency: root.getAttribute('data-currency') || 'EGP'
        });
        var btn = form.querySelector('[data-sfco-buy]');
        // Guard against a double tap creating two checkouts; the page is
        // navigating away, so the button never needs to come back.
        if (btn) setTimeout(function () { btn.disabled = true; btn.classList.add('is-busy'); }, 0);
      });
    }

    /* ------------------------------------------------ sticky price bar */
    /*
     * Shown once the hero's CTA has scrolled away, hidden again while the
     * price card or the final CTA is on screen (they already are the CTA) and
     * after the final CTA, so it never sits over the footer. Every observer
     * uses the zero threshold: with any other, a target that leaves the screen
     * fast reports once while still partly visible and never again.
     */
    var bar = root.querySelector('[data-sfco-bar]');
    var heroGone = false;
    if (bar && hero && hasIO) {
      var state = { cardIn: false, finalReached: false };
      var link = bar.querySelector('a');
      var paintBar = function () {
        var on = heroGone && !state.cardIn && !state.finalReached;
        bar.classList.toggle('is-on', on);
        root.classList.toggle('sfco--bar', on);
        bar.setAttribute('aria-hidden', on ? 'false' : 'true');
        if (link) link.tabIndex = on ? 0 : -1;
      };
      new IntersectionObserver(function (es) {
        heroGone = !es[0].isIntersecting && es[0].boundingClientRect.top < 0;
        paintBar();
        schedule();
      }).observe(hero);
      if (card) {
        new IntersectionObserver(function (es) {
          state.cardIn = es[0].isIntersecting;
          paintBar();
        }).observe(card);
      }
      if (fin) {
        new IntersectionObserver(function (es) {
          state.finalReached = es[0].isIntersecting || es[0].boundingClientRect.top < 0;
          paintBar();
        }).observe(fin);
      }
    }

    /* ------------------------------------------------ scroll-linked: progress, path, rail */
    var progress = root.querySelector('[data-sfco-progress]');
    var tl = root.querySelector('[data-sfco-tl]');
    var steps = tl ? tl.querySelectorAll('[data-sfco-tl-step]') : [];
    var rail = root.querySelector('[data-sfco-rail]');
    var railLinks = rail ? rail.querySelectorAll('[data-sfco-rail-link]') : [];
    var chapters = [];
    railLinks.forEach(function (a) {
      var el = root.querySelector(a.getAttribute('href'));
      if (el) chapters.push({ el: el, a: a });
    });
    var act1 = root.querySelector('.sfco-act1');
    var header = document.querySelector('.shopify-section-header-sticky, .section-header');
    var current = -1;
    var hdr = -1;
    var queued = false;

    function frame() {
      queued = false;
      var vh = window.innerHeight;
      var r = root.getBoundingClientRect();

      // Where the theme's sticky header ends right now (it can hide on scroll
      // down): the progress line sits under it, and the pinned headings and
      // jump targets clear it.
      var hb = header ? Math.max(0, Math.round(header.getBoundingClientRect().bottom)) : 0;
      if (hb !== hdr) {
        hdr = hb;
        root.style.setProperty('--sfco-hdr', hb + 'px');
      }

      // Reading progress: how far through this page, not the whole document,
      // so the footer does not hold the line short of full.
      if (progress) {
        var span = r.height - vh;
        var p = span > 0 ? clamp(-r.top / span) : 1;
        progress.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      }

      // The path fills toward a point 60% down the screen; each session
      // lights up once the line has reached its dot.
      if (tl) {
        var anchor = vh * 0.6;
        var tr = tl.getBoundingClientRect();
        var len = tr.height - 40;
        var fill = len > 0 ? clamp((anchor - tr.top - 20) / len) : 0;
        tl.style.setProperty('--p', reduce ? 1 : fill.toFixed(4));
        steps.forEach(function (s) {
          var dot = s.firstElementChild || s;
          var dr = dot.getBoundingClientRect();
          s.classList.toggle('is-on', reduce || dr.top + dr.height / 2 <= anchor + 1);
        });
      }

      // The rail appears once the hero has gone and before the last word;
      // the current chapter is the last one whose top has passed 40%.
      if (rail) {
        var finTop = fin ? fin.getBoundingClientRect().top : Infinity;
        var show = (hero ? hero.getBoundingClientRect().bottom < vh * 0.25 : true) && finTop > vh * 0.5;
        rail.classList.toggle('is-on', show);
        rail.classList.toggle('is-dark', !!act1 && act1.getBoundingClientRect().bottom > vh / 2);
        var idx = -1;
        for (var i = 0; i < chapters.length; i++) {
          if (chapters[i].el.getBoundingClientRect().top < vh * 0.4) idx = i;
        }
        if (idx !== current) {
          current = idx;
          chapters.forEach(function (c, j) {
            c.a.classList.toggle('is-cur', j === idx);
            if (j === idx) c.a.setAttribute('aria-current', 'step');
            else c.a.removeAttribute('aria-current');
          });
        }
      }
    }

    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(frame);
    }

    if (progress || tl || rail) {
      window.addEventListener('scroll', schedule, { passive: true });
      window.addEventListener('resize', schedule, { passive: true });
      schedule();
    }

    /* ------------------------------------------------ launch countdown */
    /* Only with a real end date set in the theme editor, and only while it is
       still ahead. Past the date it disappears rather than resetting. */
    var ymd = (root.getAttribute('data-end') || '').trim();
    var clocks = root.querySelectorAll('[data-sfco-cd]');
    if (clocks.length && /^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
      var end = cairoEndOfDay(ymd);
      var timer = null;
      var tick = function () {
        var left = Math.floor((end - Date.now()) / 1000);
        if (!(left > 0)) {
          clocks.forEach(function (c) { c.hidden = true; });
          if (timer) clearInterval(timer);
          return;
        }
        var d = Math.floor(left / 86400);
        var h = Math.floor((left % 86400) / 3600);
        var m = Math.floor((left % 3600) / 60);
        var s = left % 60;
        clocks.forEach(function (c) {
          c.querySelector('[data-u="d"]').textContent = d;
          c.querySelector('[data-u="h"]').textContent = pad(h);
          c.querySelector('[data-u="m"]').textContent = pad(m);
          c.querySelector('[data-u="s"]').textContent = pad(s);
          c.hidden = false;
        });
      };
      tick();
      if (end - Date.now() > 0) timer = setInterval(tick, 1000);
    }
  }

  function upgradeAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-sfco]');
    for (var i = 0; i < nodes.length; i++) Course(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { upgradeAll(); });
  } else {
    upgradeAll();
  }
  document.addEventListener('shopify:section:load', function (e) { upgradeAll(e.target); });
})();